import { createContext, useContext, ReactNode, useMemo } from "react";
import { useCurrentUser, getUserDisplayName, getUserFullName } from "@/lib/hooks/use-current-user";
import { User } from "@/lib/graphql/types-and-hooks";

interface UserContextType {
  currentUser: User | undefined;
  displayName: string;
  fullName: string;
  profileImageUrl: string | null | undefined;
  loading: boolean;
  error: unknown;
  refetch: () => void;
}

const UserContext = createContext<UserContextType>({
  currentUser: undefined,
  displayName: "User",
  fullName: "User",
  profileImageUrl: null,
  loading: false,
  error: undefined,
  refetch: () => {},
});

export function UserProvider({ children }: { children: ReactNode }) {
  const { currentUser, loading, error, refetch } = useCurrentUser();

  const value = useMemo(() => ({
    currentUser,
    displayName: getUserDisplayName(currentUser),
    fullName: getUserFullName(currentUser),
    profileImageUrl: currentUser?.profileImageUrl,
    loading,
    error,
    refetch,
  }), [currentUser, loading, error, refetch]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
